import { useNavigate, useLocation } from 'react-router'
import { FileText, LogOut, Home, BookCopy, ArrowLeft, FileSpreadsheet } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import NotificationBell from '@/components/NotificationBell'
import { toast } from 'react-toastify'
import apiClient from '@/services/api/api'
import { useState } from 'react'
import { ALLOWED_EXPORT_EMAILS } from '@/constants/Permissions'
import { handleError } from '@/utils/helpers'

const Header = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [isDownloading, setIsDownloading] = useState(false)

  // Check if current user can access export feature
  const canExport = user?.email && ALLOWED_EXPORT_EMAILS.includes(user.email)

  const handleMyJDs = () => {
    navigate('/my-jd')
  }

  const handleDownloadReport = async () => {
    try {
      setIsDownloading(true)
      const response = await apiClient.get('/reports/export-excel', {
        responseType: 'blob',
      })

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0]
      link.download = `untold-jd-report-${date}.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Tải báo cáo thành công!')
    } catch (error) {
      console.error('Download report error:', error)
      handleError(error, { fallbackMessage: 'Lỗi khi tải báo cáo' })
    } finally {
      setIsDownloading(false)
    }
  }

  const handleLogout = () => {
    logout()
  }

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }
    return email?.[0]?.toUpperCase() || 'U'
  }

  const isHomePage = location.pathname === '/'

  return (
    <header className="md:px-20 md:py-6 flex items-center justify-between w-full px-4 py-4">
      {/* Left Side - Home/Back Button */}
      <div className="flex items-center gap-3">
        {!isHomePage && (
          <Button
            onClick={() => navigate(-1)}
            className="outline-ring/50 active:scale-95 group bg-primary hover:scale-110 hover:shadow-xl p-3 transition-all duration-300 ease-out rounded-full shadow-md"
          >
            <ArrowLeft className="w-5 h-5 text-white transition-colors duration-200" />
          </Button>
        )}
        <Button
          onClick={() => navigate('/')}
          className="outline-ring/50 active:scale-95 group bg-primary hover:scale-110 hover:shadow-xl p-3 transition-all duration-300 ease-out rounded-full shadow-md"
        >
          <Home className="w-5 h-5 text-white transition-colors duration-200" />
        </Button>
      </div>

      {/* Notification Bell & Avatar - Right Side */}
      <div className="flex items-center gap-3">
        {/* Untold JD Button */}
        <Button
          onClick={() => navigate('/library-jd')}
          className="outline-ring/50 active:scale-95 hover:scale-105 flex items-center gap-2 h-11 px-4 text-sm font-semibold transition-all duration-300 ease-out border-2 rounded-full shadow-md bg-white text-primary border-primary hover:bg-[#FFEFF4]"
        >
          <BookCopy className="w-4 h-4" />
          <span>Untold JD của MoMoer</span>
        </Button>

        {/* Notification Bell */}
        <NotificationBell />

        {/* Avatar Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="outline-ring/50 active:scale-95 group bg-primary hover:scale-110 hover:shadow-xl w-11 h-11 p-0 transition-all duration-300 ease-out rounded-full shadow-md">
              <div className="flex items-center justify-center w-full h-full text-sm font-semibold text-white transition-colors duration-200">
                {getInitials(user?.name, user?.email)}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56 bg-white border-2 border-gray-200 shadow-2xl"
          >
            {/* User Info Header */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center w-10 h-10 font-semibold text-white rounded-full">
                  {getInitials(user?.name, user?.email)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {user?.name || user?.email}
                  </p>
                  {user?.name && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              <DropdownMenuItem
                onClick={handleMyJDs}
                className="hover:bg-purple-50 px-4 py-3 transition-colors cursor-pointer"
              >
                <FileText className="w-4 h-4 mr-3 text-purple-600" />
                <span className="font-medium text-gray-900">JD của tôi</span>
              </DropdownMenuItem>

              {canExport && (
                <>
                  <DropdownMenuItem
                    onClick={handleDownloadReport}
                    disabled={isDownloading}
                    className="hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-3 transition-colors cursor-pointer"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-3 text-green-600" />
                    <span className="font-medium text-gray-900">
                      {isDownloading ? 'Đang tải...' : 'Tải báo cáo Excel'}
                    </span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator className="my-1 bg-gray-200" />
                </>
              )}

              {!canExport && <DropdownMenuSeparator className="my-1 bg-gray-200" />}

              <DropdownMenuItem
                onClick={handleLogout}
                className="hover:bg-red-50 px-4 py-3 transition-colors cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-3 text-red-600" />
                <span className="font-medium text-red-600">Đăng xuất</span>
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}

export default Header
